import React, { useState, useEffect } from 'react';
import { useParams, Link } from 'react-router-dom';
import { PublicHeader, PublicFooter } from '../components/PublicLayout';
import { Card, CardContent } from '../components/ui/card';
import { Badge } from '../components/ui/badge';
import { Skeleton } from '../components/ui/skeleton';
import { Tag, Clock } from 'lucide-react';
import api from '../lib/api';
import { formatDisplayViews } from '../lib/view-count';

export default function TagPage() {
  const { tag } = useParams();
  const [articles, setArticles] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    setLoading(true);
    api.get(`/api/topics/${encodeURIComponent(tag)}`)
      .then(r => setArticles(r.data.articles || []))
      .catch(() => {})
      .finally(() => setLoading(false));
  }, [tag]);

  return (
    <>
      <PublicHeader />
      <main className="bg-[#FBF7EF] min-h-screen">
        <section className="max-w-[1100px] mx-auto px-4 sm:px-6 py-12">
          <div className="flex items-center gap-3 mb-8">
            <div className="w-10 h-10 rounded-xl bg-[#121316] flex items-center justify-center">
              <Tag className="w-5 h-5 text-white" />
            </div>
            <div>
              <h1 className="text-2xl font-bold capitalize">{tag}</h1>
              <p className="text-sm text-[#5B616E]">{articles.length} articles tagged</p>
            </div>
          </div>

          {loading ? (
            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">{[1,2,3,4].map(i => <Skeleton key={i} className="h-40 rounded-xl" />)}</div>
          ) : articles.length === 0 ? (
            <Card className="bg-white border-[#E7E0D6] p-12 text-center">
              <p className="text-[#5B616E]">No articles found with tag "{tag}"</p>
            </Card>
          ) : (
            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
              {articles.map(article => (
                <Link key={article.id} to={`/article/${article.id}`} className="block">
                  <Card className="bg-white border-[#E7E0D6] hover:shadow-lg transition-shadow overflow-hidden h-full">
                    {article.featured_image && (
                      <img src={article.featured_image} alt="" className="w-full h-40 object-cover" />
                    )}
                    <CardContent className="pt-4">
                      <h2 className="font-bold text-base line-clamp-2 hover:text-[#1E5A8A] transition-colors">{article.title}</h2>
                      {article.subtitle && <p className="text-sm text-[#5B616E] mt-1 line-clamp-2">{article.subtitle}</p>}
                      <div className="flex items-center gap-3 mt-3 text-xs text-[#5B616E]">
                        <span>{article.author_display_name || 'Staff'}</span>
                        <span className="flex items-center gap-1"><Clock className="w-3 h-3" />{article.reading_time || 3} min</span>
                        <span>{formatDisplayViews(article.views, article.id)} views</span>
                      </div>
                      {article.tags?.length > 0 && (
                        <div className="flex flex-wrap gap-1 mt-3">
                          {article.tags.slice(0, 4).map(t => (
                            <Badge key={t} variant="secondary" className="text-[10px]">{t}</Badge>
                          ))}
                        </div>
                      )}
                    </CardContent>
                  </Card>
                </Link>
              ))}
            </div>
          )}
        </section>
      </main>
      <PublicFooter />
    </>
  );
}
